/**
 * The first-load splash lives in index.html, so it shows before the bundle arrives. It stays until
 * the first screen is really ready: the router has rendered the first page (a lazy page included),
 * its data has arrived and the pictures in view have loaded. A slow network never keeps it longer
 * than MAX_WAIT_MS from the page start; the page's own skeletons take over from there.
 */

const MAX_WAIT_MS = 4000;
const CHECK_EVERY_MS = 100;

interface FirstScreen {
  /** The router has rendered the first page. */
  isRendered: () => boolean;
  /** Requests for the page's data are still running. */
  isFetching: () => boolean;
}

export function hideSplashWhenReady(screen: FirstScreen, now = () => performance.now()): void {
  const check = () => {
    // a check in the same frame as the first render would miss the requests its effects start
    const ready = screen.isRendered() && !screen.isFetching() && visiblePicturesLoaded();
    if (ready || now() >= MAX_WAIT_MS) {
      hideSplash();
      return;
    }
    setTimeout(check, CHECK_EVERY_MS);
  };
  setTimeout(check, CHECK_EVERY_MS);
}

/** Pictures on the first screen (not the splash's own logo) have loaded or failed. */
function visiblePicturesLoaded(): boolean {
  const root = document.getElementById("root");
  if (!root) return true;
  return Array.from(root.querySelectorAll("img")).every((img) => {
    if (img.complete) return true;
    const box = img.getBoundingClientRect();
    const inView = box.bottom > 0 && box.top < window.innerHeight && box.width > 0;
    return !inView;
  });
}

export function hideSplash(): void {
  const splash = document.getElementById("splash");
  if (!splash) return;
  // remove it even when no transition runs (hidden tab, reduced motion)
  setTimeout(() => {
    splash.remove();
  }, 400);
  // let the first page paint under the splash, then fade it away
  requestAnimationFrame(() => {
    splash.classList.add("splash-done");
    splash.addEventListener(
      "transitionend",
      () => {
        splash.remove();
      },
      { once: true },
    );
  });
}
